import { Receipt as ReceiptIcon, ErrorOutline as ErrorIcon, CheckCircle as CheckIcon } from '@mui/icons-material';
import { 
  Box, 
  Card, 
  Chip, 
  Divider, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Typography,
  CircularProgress
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PaymentHistoryItem } from '@/backend/contracts/billing';

import { useFirebase } from '@/firebase';
import { formatPrice } from '@/lib/subscription-utils';


export function PaymentHistory() {
  const { backend, user } = useFirebase();
  const { t } = useTranslation();
  const [payments, setPayments] = useState<PaymentHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPayments([]);

    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    let disposed = false;
    let syncStarted = false;
    const unsubscribe = backend.billing.subscribePaymentHistory(user.uid, (paymentsData) => {
      if (disposed) return;

      setPayments(paymentsData);
      if (paymentsData.length > 0) setLoading(false);
      if (syncStarted) return;
      syncStarted = true;

      void backend.billing.syncPaymentHistory()
        .then((synced) => {
          if (!disposed && synced === 0) setLoading(false);
        })
        .catch((error) => {
          console.error('Error syncing payment history:', error);
          if (!disposed) setLoading(false);
        });
    });

    return () => {
      disposed = true;
      unsubscribe();
    };
  }, [backend, user]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (payments.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          {t('profile.noPayments')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ReceiptIcon fontSize="small" />
        {t('profile.paymentHistory')}
      </Typography>
      
      <Card variant="outlined" sx={{ bgcolor: 'background.paper', borderColor: 'divider' }}>
        <List sx={{ p: 0 }}>
          {payments.map((payment, index) => {
            const date = new Date(payment.timestamp);
            const isSucceeded = payment.status === 'succeeded';

            return (
              <Box key={payment.id}>
                <ListItem sx={{ py: 2 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {isSucceeded ? (
                      <CheckIcon color="success" sx={{ fontSize: 20 }} />
                    ) : (
                      <ErrorIcon color="error" sx={{ fontSize: 20 }} />
                    )}
                  </ListItemIcon>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1" fontWeight={500}>
                          {payment.planName}
                        </Typography>
                        <Typography variant="body1" fontWeight={700}>
                          {formatPrice(payment.amount, payment.currency)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </Typography>
                        <Chip 
                          label={payment.status.toUpperCase()} 
                          size="small" 
                          variant="outlined"
                          color={isSucceeded ? 'success' : 'error'}
                          sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }}
                        />
                      </Box>
                    }
                  />
                </ListItem>
                {index < payments.length - 1 && <Divider sx={{ opacity: 0.5 }} />}
              </Box>
            );
          })}
        </List>
      </Card>
    </Box>
  );
}
